import {
  featureCollectionSchema,
  featureSchema,
  multiPolygonSchema,
  regionCollectionSchema
} from 'api-validations/geojson-schemas'
import { path } from 'utils'
import { z } from 'zod'

import { OperationLevelPermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { commaSeparatedNumbers } from '../zod-extra'
import { type LayerScopedPathParams, layerScopedPathSchema } from './shared'

/**
 * Wire-level shape of a single include/exclude filter entry, matching the
 * stitch `LassoFilter::Filter` struct. The client-side editor type
 * ({@link DataFilter}) adds a `type` field to distinguish include vs. exclude,
 * but on the wire that's expressed by which array the entry lives in.
 */
/**
 * The C++ `Range` struct technically allows `bounds` and `blue` to be set
 * independently, but in practice exactly one is populated per entry — discrete
 * filters carry `blue`, continuous filters carry `bounds`. The discriminated
 * union here mirrors that intent so callers can narrow at the type level.
 */
const lassoFilterRangeSchema = z
  .object({ bounds: z.object({ minimum: z.number(), maximum: z.number() }) })
  .or(z.object({ blue: z.number() }))

export const lassoFilterEntrySchema = z.object({
  streamId: z.string(),
  applicationId: z.string(),
  layerId: z.string(),
  range: lassoFilterRangeSchema
})

export type LassoFilterEntry = z.infer<typeof lassoFilterEntrySchema>

/**
 * Body shape shared by `/lasso/histogram`, `/lasso/summation`, and
 * `/lasso/bin/{independent}`. Mirrors the C++ `LassoFilter` — a polygon plus
 * include/exclude filter arrays — and is undefined when neither is selected.
 */
export const lassoFilterBodySchema = z
  .object({
    polygon: regionCollectionSchema.optional(),
    include: z.array(lassoFilterEntrySchema).optional(),
    exclude: z.array(lassoFilterEntrySchema).optional()
  })
  .or(z.undefined())

const round = (num: number) => Math.floor(num * 10) / 10

const limitSchema = z.object({
  actual: z.number().transform(round),
  filtered: z.number().transform(round)
})

export const lassoSummarySchema = z
  .object({
    average: z.number().transform(round),
    minimum: limitSchema,
    maximum: limitSchema,
    units: z.string().optional()
  })
  .transform((data) => ({ ...data, hasData: true as const }))
  .or(
    z
      .object({
        units: z.string().optional(),
        minimum: z.undefined().optional(),
        maximum: z.undefined().optional(),
        average: z.undefined().optional()
      })
      .transform((data) => ({ ...data, hasData: false as const }))
  )

const discreteDataPointSchema = z
  .object({
    acres: z.number(),
    blue: z.number(),
    label: z.string(),
    num_points: z.number()
  })
  .or(
    z.object({
      acres: z.number(),
      blue: z.number(),
      value: z.number(),
      num_points: z.number()
    })
  )

const continuousHistogramDataSchema = z.object({
  acres: z.number(),
  discrete: z.literal(false),
  bins: z.array(
    z.union([
      z.object({
        acres: z.number(),
        maximum: z.number().transform(round),
        minimum: z.number().transform(round),
        num_points: z.number()
      }),
      z.object({
        acres: z.number(),
        maximum: z.number().transform(round),
        num_points: z.number()
      }),
      z.object({
        acres: z.number(),
        minimum: z.number().transform(round),
        num_points: z.number()
      }),
      discreteDataPointSchema
    ])
  ),
  num_points: z.number(),
  units: z.string().optional()
})

const discreteHistogramDataSchema = z.object({
  acres: z.number(),
  discrete: z.literal(true),
  bins: z.array(discreteDataPointSchema),
  num_points: z.number(),
  units: z.string().optional()
})

const lassoHistogramSchema = z.discriminatedUnion('discrete', [
  discreteHistogramDataSchema,
  continuousHistogramDataSchema
])

type QueryParams = {
  maximum: number
  minimum: number
  bins: number
}

export type LassoRequestBody = z.infer<typeof lassoFilterBodySchema>

export const lassoDiscreteHistogramApi = defineApi({
  pathSchema: layerScopedPathSchema,
  name: 'GetDiscreteLassoHistogram',
  description:
    'Get histogram data for a lasso selection showing distribution of values across bins for a discrete metric',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  pathFor: (p: LayerScopedPathParams) =>
    path(
      `${p.requestType}/streams/${p.orgCode}/${p.streamId}/layers/${p.applicationId}/${p.layerId}/lasso/histogram` as const
    ),
  responseSchema: discreteHistogramDataSchema,
  requestBodySchema: lassoFilterBodySchema,
  method: 'POST'
})

export const lassoContinuousHistogramApi = defineApi({
  pathSchema: layerScopedPathSchema,
  querySchema: z.object({
    maximum: z.number(),
    minimum: z.number(),
    bins: z.number()
  }),
  name: 'GetContinuousLassoHistogram',
  description:
    'Get histogram data for a lasso selection showing distribution of values across bins for a continuous metric',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  pathFor: (p: LayerScopedPathParams & QueryParams) =>
    path(
      `${p.requestType}/streams/${p.orgCode}/${p.streamId}/layers/${p.applicationId}/${p.layerId}/lasso/histogram` as const,
      {
        maximum: p.maximum,
        minimum: p.minimum,
        bins: p.bins
      }
    ),
  responseSchema: continuousHistogramDataSchema,
  requestBodySchema: lassoFilterBodySchema,
  method: 'POST'
})

export const lassoSummaryApi = defineApi({
  pathSchema: layerScopedPathSchema,
  pathFor: (p: LayerScopedPathParams) =>
    `${p.requestType}/streams/${p.orgCode}/${p.streamId}/layers/${p.applicationId}/${p.layerId}/lasso/summation`,
  name: 'GetLassoSummary',
  description: 'Get summary statistics (average, min, max) for a lasso selection',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema: lassoSummarySchema,
  requestBodySchema: lassoFilterBodySchema,
  method: 'POST'
})

const zoningScoreSchema = z.object({
  noDataAcres: z.number(),
  reassignedFraction: z.number(),
  varianceExplained: z.number(),
  summary: z.array(
    z.object({ zone: z.number(), acres: z.number(), mean: z.number(), stdDev: z.number() })
  )
})

const lassoZonesSchema = zoningScoreSchema.extend({
  zones: featureCollectionSchema(
    featureSchema({
      geometry: multiPolygonSchema,
      properties: z.object({ zone: z.number() })
    })
  ),
  cellSize: z.number(),
  units: z.string().nullable()
})

const lassoZoneScoresSchema = z.object({
  candidates: z.array(zoningScoreSchema.extend({ breaks: z.array(z.number()) })),
  cellSize: z.number(),
  units: z.string().nullable()
})

const breakSetsCodec = z.codec(z.string(), z.array(z.array(z.number())), {
  decode: (joined) =>
    joined === '' ? [] : joined.split(';').map((breaks) => breaks.split(',').map(Number)),
  encode: (sets) => sets.map((breaks) => breaks.join(',')).join(';')
})

const zoneSizesSchema = z.object({
  minZoneSize: z.number().optional(),
  minFeature: z.number().optional(),
  cellSize: z.number().optional()
})

export const lassoZoneScoresApi = defineApi({
  pathSchema: layerScopedPathSchema,
  querySchema: zoneSizesSchema.extend({ candidates: breakSetsCodec }),
  name: 'GetLassoZoneScores',
  description:
    'Compare how well several zone counts would split a continuous layer within a lasso selection before drawing any zones, such as to recommend how many zones a prescription should have',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  pathFor: ({ requestType, orgCode, streamId, applicationId, layerId, candidates, ...sizes }) =>
    path(
      `${requestType}/streams/${orgCode}/${streamId}/layers/${applicationId}/${layerId}/lasso/zones/scores` as const,
      { candidates: breakSetsCodec.encode(candidates), ...sizes }
    ),
  responseSchema: lassoZoneScoresSchema,
  requestBodySchema: lassoFilterBodySchema,
  method: 'POST'
})

export const lassoZonesApi = defineApi({
  pathSchema: layerScopedPathSchema,
  querySchema: zoneSizesSchema.extend({
    breaks: commaSeparatedNumbers,
    simplify: z.number().optional(),
    smooth: z.number().optional()
  }),
  name: 'GetLassoZones',
  description:
    'Draw zones from a continuous layer within a lasso selection once the breaks between zones are chosen, such as to place prescription zones on the map',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  pathFor: ({ requestType, orgCode, streamId, applicationId, layerId, breaks, ...sizes }) =>
    path(
      `${requestType}/streams/${orgCode}/${streamId}/layers/${applicationId}/${layerId}/lasso/zones` as const,
      { breaks: commaSeparatedNumbers.encode(breaks), ...sizes }
    ),
  responseSchema: lassoZonesSchema,
  requestBodySchema: lassoFilterBodySchema,
  method: 'POST'
})

export type LassoSummation = z.infer<typeof lassoSummarySchema>
export type LassoHistogram = z.infer<typeof lassoHistogramSchema>

type UnknownBin = z.infer<typeof continuousHistogramDataSchema>['bins'][number]
type DiscreteBin = z.infer<typeof discreteDataPointSchema>

export const isDiscreteBin = (bin: UnknownBin): bin is DiscreteBin => 'label' in bin
